import React from 'react';
import {
  Link,
} from 'react-router-dom';
import {
  VirtualTableColumnDef,
} from 'core/components/VirtualTable/types';
import VirtualTable from 'core/components/VirtualTable';
import ToolTip from 'core/components/Tooltip';
import LinkIcon from 'core/icons/link';
import Tag from 'core/components/Tag';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  PatientAuthorizationClaimUsageStatus,
} from 'dtos/patientAuthorizationClaimUsageStatus';
import {
  PatientAuthorizationUsageScope,
} from 'dtos/patientAuthorizationUsageScope';
import type {
  PatientAuthorizationClaimUsageResponseDTO,
  PatientAuthorizationResponseDTO,
} from 'dtos';
import {
  formatTwoDigitDate,
} from 'utils/date';
import {
  notEmptyString,
} from '@populateui/utils';

type Allowance = PatientAuthorizationResponseDTO['allowances'][number];
type Usage = PatientAuthorizationClaimUsageResponseDTO;

const USAGE_SCOPE_LABELS: Record<PatientAuthorizationUsageScope, string> = {
  [PatientAuthorizationUsageScope.ClaimServiceLine]: 'Service',
  [PatientAuthorizationUsageScope.ClaimLevelBox23]: 'Claim',
};

const tableClasses = {
  root: '!pb-0',
  body: '!m-0 !p-0',
  scrollContainer: '!pb-0',
  header: '!justify-start',
  row: 'hover:before:!hidden !justify-start',
  outerRow: '!w-full !border-x-0 !border-t-0 hover:!border-b-gray-200',
  scrollbarGutter: 'initial',
};

type AuthorizationBreakdownTableProps<T> = {
  columns: VirtualTableColumnDef<T>[];
  data: T[];
  getRowId: (row: T, index: number) => string;
};

function AuthorizationBreakdownTable<T>({
  columns,
  data,
  getRowId,
}: AuthorizationBreakdownTableProps<T>) {
  return (
    <div
      className="flex w-full flex-col overflow-hidden rounded border populate-breakdown-table"
    >
      <VirtualTable<T>
        columns={columns}
        data={data}
        getRowId={getRowId}
        classes={tableClasses}
        enableRowSelection={false}
      />
    </div>
  );
}

type BillingCodeWithDescriptionProps = {
  code: string;
  description: string;
};

function BillingCodeWithDescription({ code, description }: BillingCodeWithDescriptionProps) {
  return (
    <ToolTip
      title={description}
      placement="top-start"
      className="whitespace-normal break-words"
      popperClassName="code-description-tooltip"
      classes={{ tooltip: 'max-h-[300px] overflow-auto' }}
      isInteractive
    >
      <span className="cursor-help">{code}</span>
    </ToolTip>
  );
}

const quantityColumn = (
  id: keyof Pick<Allowance,
    'approvedQuantity' | 'usedQuantity' | 'externalUsedQuantity' | 'remainingQuantity'>,
  title: string,
  size: number,
): VirtualTableColumnDef<Allowance> => ({
  id,
  accessorKey: id,
  columnTitle: <span className="block w-full text-left">{title}</span>,
  size,
  minSize: size,
  className: '!justify-start text-left',
  cell: ({ row }) => <span className="w-full text-left">{row.original[id]}</span>,
});

const allowanceColumns: VirtualTableColumnDef<Allowance>[] = [
  {
    id: 'allowanceBasis',
    accessorKey: 'allowanceBasis',
    columnTitle: 'Basis',
    size: 85,
    minSize: 85,
    meta: { autoSized: true },
    className: 'font-medium text-gray-700 text-xs',
  },
  {
    id: 'codes',
    columnTitle: <span className="block w-full text-left">CPT codes</span>,
    multiLineHeader: true,
    size: 75,
    minSize: 75,
    meta: { autoSized: true },
    className: 'text-gray-600',
    cell: ({ row }) => row.original.codes.length > 0
      ? (
        <div className="flex flex-col">
          {row.original.codes.map(({ code, description }) => (
            <BillingCodeWithDescription
              key={`${code}-${description}`}
              code={code}
              description={description}
            />
          ))}
        </div>
      )
      : DEFAULT_EMPTY_VALUE,
  },
  quantityColumn('approvedQuantity', 'Approved', 76),
  {
    ...quantityColumn('usedQuantity', 'Used', 76),
    multiLineHeader: true,
  },
  {
    ...quantityColumn('externalUsedQuantity', 'Prior', 83),
    multiLineHeader: true,
  },
  {
    id: 'totalUsedQuantity',
    accessorFn: (allowance) => allowance.usedQuantity + allowance.externalUsedQuantity,
    columnTitle: <span className="block w-full text-left">Total used</span>,
    size: 76,
    minSize: 76,
    multiLineHeader: true,
    className: '!justify-start text-left',
    cell: ({ getValue }) => <span className="w-full text-left">{getValue<number>()}</span>,
  },
  quantityColumn('remainingQuantity', 'Remaining', 76),
];

type AuthorizationAllowanceTableProps = {
  allowances: PatientAuthorizationResponseDTO['allowances'];
};

export function AuthorizationAllowanceTable({
  allowances,
}: AuthorizationAllowanceTableProps) {
  return allowances.length === 0
    ? <p className="text-sm text-gray-500">{DEFAULT_EMPTY_VALUE}</p>
    : (
      <section className="min-w-0">
        <AuthorizationBreakdownTable
          columns={allowanceColumns}
          data={allowances}
          getRowId={(allowance) => String(allowance.patientAuthorizationAllowanceId)}
        />
      </section>
    );
}

type UsageClaimProps = {
  usage: Usage;
};

function UsageClaim({ usage }: UsageClaimProps) {
  const { claimId } = usage;
  const claimLabel = notEmptyString(usage.claimExternalId)
    ? usage.claimExternalId
    : claimId;

  return (
    <div className="flex w-full min-w-0 items-center gap-2">
      <Link
        to={`/billing/claims/${claimId}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Opens in a new tab"
        className="flex min-w-0 items-center gap-1 font-semibold text-blue-600 hover:underline"
      >
        <span className="truncate">{claimLabel}</span>
        <LinkIcon aria-hidden="true" className="!h-3 !w-3 shrink-0" />
      </Link>
      {notEmptyString(usage.claimStatus) && (
        <Tag
          tagName={usage.claimStatus ?? ''}
          className="ml-auto shrink-0 whitespace-nowrap !px-3 !py-0 !font-medium max-h-[20px]"
          defaultColor="!bg-brown-100 !text-brown-700"
        />
      )}
    </div>
  );
}

function getUsageColumns(descriptions: Record<string, string>): VirtualTableColumnDef<Usage>[] {
  return [
    {
      id: 'serviceDate',
      columnTitle: 'DOS',
      size: 78,
      minSize: 78,
      cell: ({ row }) => row.original.serviceDate
        ? formatTwoDigitDate(row.original.serviceDate)
        : DEFAULT_EMPTY_VALUE,
    },
    {
      id: 'code',
      columnTitle: 'Code',
      size: 65,
      minSize: 65,
      cell: ({ row }) => {
        const { code } = row.original;
        const description = code ? descriptions[code.toUpperCase()] : undefined;
        return code && description
          ? <BillingCodeWithDescription code={code} description={description} />
          : code ?? DEFAULT_EMPTY_VALUE;
      },
    },
    {
      id: 'claim',
      columnTitle: 'Claim',
      size: 155,
      minSize: 155,
      meta: { autoSized: true },
      cell: ({ row }) => <UsageClaim usage={row.original} />,
    },
    {
      id: 'quantityUsed',
      columnTitle: 'Qty',
      size: 40,
      minSize: 40,
      className: '!justify-end',
      cell: ({ row }) => row.original.quantityUsed,
    },
    {
      id: 'authorizationUsageStatus',
      columnTitle: 'Counted',
      size: 70,
      minSize: 70,
      cell: ({ row }) => row.original.authorizationUsageStatus === PatientAuthorizationClaimUsageStatus.Counted
        ? 'Yes'
        : 'No',
    },
    {
      id: 'usageScope',
      columnTitle: <span className="block w-full text-right">Scope</span>,
      size: 65,
      minSize: 65,
      className: '!justify-end text-right',
      cell: ({ row }) => USAGE_SCOPE_LABELS[row.original.usageScope],
    },
  ];
}

type AuthorizationUsageLedgerProps = {
  usages: Usage[];
  descriptions: Record<string, string>;
};

export function AuthorizationUsageLedger({
  usages,
  descriptions,
}: AuthorizationUsageLedgerProps) {
  return (
    <section className="min-w-0">
      <h2 className="mb-2 text-sm font-semibold text-gray-600">Usage ledger</h2>
      {usages.length > 0 ? (
        <AuthorizationBreakdownTable
          columns={getUsageColumns(descriptions)}
          data={usages}
          getRowId={(usage, index) => `${usage.claimId}-${usage.visitNoteBillingCodeId ?? 'claim'}-${usage.code ?? 'visit'}-${index}`}
        />
      ) : (
        <p className="text-xs text-gray-500">No usage records</p>
      )}
    </section>
  );
}
